/**
 * raw fetch/JSON 오류 메시지를 사용자 친절 한국어로 변환.
 * - "Failed to fetch" / TypeError → 서버 연결 안내
 * - "Unexpected token '<', ... is not valid JSON" → HTML fallback (백엔드 미응답)
 * - 404 / 5xx → 상태 코드 안내
 * - 그 외 → 기본 안내 + 짧은 원문 (디버그)
 */
export function friendlyErrorMessage(err, fallback = "데이터를 불러오지 못했습니다.") {
  const raw = err && err.message ? String(err.message) : String(err || "");
  if (!raw) return fallback;
  if (/Failed to fetch|NetworkError|ERR_NETWORK|ENOTFOUND|ECONNREFUSED/i.test(raw)) {
    return "서버에 연결할 수 없습니다. 백엔드 실행 후 다시 시도해 주세요.";
  }
  // Browser/Node JSON.parse 메시지 (HTML fallback 등)
  if (/Unexpected token|not valid JSON|JSON\.parse|Unexpected end of JSON/i.test(raw)) {
    return "서버 응답을 해석할 수 없습니다. 백엔드 API 연결 상태를 확인해 주세요.";
  }
  const m = /HTTP\s*(\d{3})/i.exec(raw);
  if (m) {
    const code = Number(m[1]);
    if (code === 401 || code === 403) return "접근 권한이 없습니다. 다시 로그인해 주세요.";
    if (code === 404) return "요청한 데이터를 찾을 수 없습니다.";
    if (code >= 500) return "서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.";
    return `요청을 처리하지 못했습니다 (HTTP ${code}).`;
  }
  // 서버가 던진 한국어 detail(사용자용 메시지, 예: "이미 서명 완료됨")은 그대로 노출.
  // 영어/코드성 raw 만 폴백으로 대체 — 유용한 서버 안내가 묻히지 않게.
  if (/[가-힣]/.test(raw)) return raw;
  return fallback;
}

/**
 * console.warn 형태로 친절 변환된 메시지 + 원문 디테일을 같이 남긴다.
 * Sentry 등이 붙기 전 단계의 가벼운 진단 로그.
 */
export function logFriendlyWarn(scope, err) {
  const friendly = friendlyErrorMessage(err);
  if (typeof console !== "undefined" && console.warn) {
    console.warn(`[${scope}] ${friendly}`, err);
  }
}

/**
 * fetch + JSON 파싱을 한 묶음으로 안전하게 처리.
 * - res.json() 이 reject 되더라도 (HTML fallback / 잘린 응답) 단일 catch 로 흡수
 * - HTML 컨텐츠 타입은 미리 차단 (defensive — 일부 환경에서 json() 가 lazy reject 됨)
 * - 404 / non-ok / network reject 를 일관된 Error 로 throw
 *
 * 호출자는 try/catch 로 받아서 friendlyErrorMessage(e) 로 표시.
 *
 * @param {(url: string, opts?: object) => Promise<Response>} fetchFn — apiFetch 등
 * @param {string} url
 * @param {object} [opts]
 * @returns {Promise<any>} parsed JSON
 */
export async function safeFetchJson(fetchFn, url, opts) {
  const res = await fetchFn(url, opts);
  if (!res) {
    throw new Error("응답이 비어 있습니다.");
  }
  if (res.status === 404) {
    const err = new Error("HTTP 404");
    err.status = 404;
    throw err;
  }
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  // Content-Type 가 명시되어 있고 HTML 이면 즉시 차단 (서버가 SPA fallback 등을 반환한 경우)
  const ct = res.headers && typeof res.headers.get === "function" ? res.headers.get("content-type") : null;
  if (ct && /text\/html|application\/xhtml/i.test(ct)) {
    throw new SyntaxError("Unexpected token '<' — HTML response, not valid JSON");
  }
  // res.json() 자체가 reject 되어도 동일 catch 로 흡수.
  return await res.json();
}
